import { buildAccentPalette } from '../containers/CreateSendContainer/Contents/styles/accentPalette';
import variables from '../contexts/ThemeProvider/variables';
import { MARKETPLACE_CURRENCY_CODE } from '../types/marketplace';
import { getCountLabel } from '../utils/helpers';
import { isSupplierTheBrand, SupplierMatchEnum, toSentenceName } from '../utils/marketplaceSupplier';
import { ContentsSourceEnum } from './createSendContents';

export const MARKETPLACE_ACCENT = variables.marketplaceAccent;
export const MARKETPLACE_ACCENT_HOVER = variables.marketplaceAccentHover;

export const MARKETPLACE_ACCENT_PALETTE = buildAccentPalette(MARKETPLACE_ACCENT, MARKETPLACE_ACCENT_HOVER);

export const MARKETPLACE_LOCK_HINT_BG = MARKETPLACE_ACCENT_PALETTE.lockHintBg;

export const MAX_MARKETPLACE_ITEMS_PER_PAGE = 24;

export const MAX_NARROWED_AUTO_LOAD_PAGES = 5;

export const MARKETPLACE_SEARCH_PLACEHOLDER = 'Search marketplace...';
export const MARKETPLACE_SOURCE_LABEL = 'Marketplace';
export const MARKETPLACE_SHIPS_FROM_LABEL = 'Ships from the brand';
export const CLEAR_KIT_ACTION_LABEL = 'Clear kit';

export const KIT_LOCK_ADD_TOOLTIPS: Record<ContentsSourceEnum, string> = {
  [ContentsSourceEnum.Marketplace]: 'Your kit has marketplace items - clear it to add inventory items.',
  [ContentsSourceEnum.IeCatalog]: 'Your kit has inventory items - clear it to add marketplace items.',
};

export const MARKETPLACE_EMPTY_CATALOG_MESSAGE = 'No marketplace items available';
export const MARKETPLACE_EMPTY_FILTERED_MESSAGE = 'No marketplace items match your search';

export const MARKETPLACE_ERROR_MESSAGE = "We couldn't load the marketplace catalog";

export interface IKitLockHint {
  lead: string;
  /** The emphasised span. */
  bold: string;
  tail: string;
}

export const KIT_LOCK_HINTS: Record<ContentsSourceEnum, IKitLockHint> = {
  [ContentsSourceEnum.Marketplace]: {
    lead: 'Your kit has ',
    bold: 'marketplace items',
    tail: '. Clear it to add IE catalog items.',
  },
  [ContentsSourceEnum.IeCatalog]: {
    lead: 'Your kit has ',
    bold: 'IE catalog items',
    tail: '. Clear it to add marketplace items.',
  },
};

const SUPPLIER_LOCK_HINT_TAIL = ' — clear to add from other suppliers';

export const getSupplierLockHint = (supplierName?: string): IKitLockHint =>
  supplierName
    ? { lead: 'Shipping from ', bold: toSentenceName(supplierName), tail: SUPPLIER_LOCK_HINT_TAIL }
    : { lead: 'Shipping from one supplier', bold: '', tail: SUPPLIER_LOCK_HINT_TAIL };

export const getSupplierLockTooltip = (itemSupplierName?: string, lockedSupplierName?: string): string => {
  const itemPart = itemSupplierName ? `Ships from ${toSentenceName(itemSupplierName)}` : 'Different supplier';
  const lockedPart = lockedSupplierName ? toSentenceName(lockedSupplierName) : 'another supplier';

  return `${itemPart} — your kit ships from ${lockedPart}`;
};

export const getSupplierAttributionTooltip = (brand: string, supplierName?: string): string =>
  isSupplierTheBrand(brand, supplierName)
    ? `Sold and shipped by ${toSentenceName(brand)}`
    : `Sold by ${toSentenceName(brand)} · ships from ${toSentenceName(supplierName ?? '')}`;

export const MARKETPLACE_SHIPS_FROM_PREFIX = 'Ships from ';

export const getSupplierOnlyChipLabel = (supplierName?: string): string =>
  supplierName ? `${toSentenceName(supplierName)} only` : 'This supplier only';

export const SUPPLIER_MATCH_LABEL: Record<SupplierMatchEnum, string> = {
  [SupplierMatchEnum.Same]: 'Same supplier',
  [SupplierMatchEnum.Different]: 'Different supplier',
};

export const MARKETPLACE_CATALOG_STALE_TIME = 5 * 60 * 1000;

// Re-exported so there is one literal: `types/` cannot import this module (theme/container deps).
export const MARKETPLACE_CURRENCY = MARKETPLACE_CURRENCY_CODE;

export const GIFT_BUDGET_COPY = {
  toggleLabel: 'Give recipients a budget',
  activeTitle: 'Recipient budget',
  activeSubtitle: 'Recipient picks any items they want',
  poolNote: 'Picks come from the Marketplace · US addresses only',
  amountLabel: 'Budget',
  errors: {
    invalid: 'Enter an amount greater than 0',
  },
};

export const getFitsBudgetTooltip = (formattedBudget: string): string =>
  `In your recipient's swap pool (up to ${formattedBudget})`;

export const GIFT_BUDGET_EMPTY_STATE_TITLE = 'Open recipient budget';
export const getGiftBudgetEmptyStateBody = (formattedBudget: string): string =>
  `Recipients choose anything up to ${formattedBudget} from the Marketplace. Add items to feature picks, or continue as is.`;
// Toggle on, no amount yet: the default placeholder would say to turn on an already-on budget.
export const GIFT_BUDGET_PENDING_EMPTY_STATE_BODY =
  'Enter an amount below — recipients will choose anything up to it from the Marketplace.';

/** "Brand · via Supplier · N options". */
export const getMarketplaceOptionsEyebrow = (
  vendor: string | undefined,
  optionCount: number,
  supplierName?: string,
): string => {
  const source = vendor?.trim() || MARKETPLACE_SOURCE_LABEL;
  const parts = [source];

  if (supplierName && !isSupplierTheBrand(source, supplierName)) {
    parts.push(`via ${toSentenceName(supplierName)}`);
  }

  if (optionCount > 1) {
    parts.push(getCountLabel(optionCount, 'option', 'options'));
  }

  return parts.join(' · ');
};

export const DEFAULT_GIFT_BUDGET_AMOUNT = 100;

/** Ceiling for a per-recipient budget; keeps a fat-fingered paste out of the payload. */
export const MAX_GIFT_BUDGET_AMOUNT = 10000;
